import { type ResourceItemDTO, type ResourceHeaderDTO, type UnitDTO } from '@/src/ui';

export const filterSlidesData = (
    grade: string,
    slidesData: ResourceItemDTO[],
    language?: string
): ResourceItemDTO[] => {
    //GK, G3, and G6 Spanish: filter out all slides
    if ((grade === 'K' || grade === '3' || grade === '6') && language === 'ES') {
        return [];
    }

    switch (grade) {
        case 'K':
            return slidesData.filter((item) => {
                return item.key && (item.key.includes('Unit6') || item.key.includes('Unit8'));
            });
        case '1':
            return slidesData.filter((item) => {
                return (
                    item.key &&
                    (item.key.includes('Unit3') ||
                        item.key.includes('Unit5') ||
                        item.key.includes('Unit11'))
                );
            });
        case '2':
            return slidesData.filter((item) => item.key && item.key.includes('Unit5'));
        case '3':
            return slidesData.filter((item) => {
                return (
                    item.key &&
                    (item.key.includes('Unit6') ||
                        item.key.includes('Unit7') ||
                        item.key.includes('Unit8'))
                );
            });
        case '4':
            return slidesData.filter((item) => {
                return (
                    item.key &&
                    (item.key.includes('Unit5') ||
                        item.key.includes('Unit6') ||
                        item.key.includes('Unit7'))
                );
            });
        case '5':
            return slidesData.filter((item) => {
                return (
                    item.key &&
                    (item.key.includes('Unit5') ||
                        item.key.includes('Unit6') ||
                        item.key.includes('Unit8'))
                );
            });
        case '6':
            return slidesData.filter((item) => item.key && item.key.includes('Unit10'));
        case '6M':
            return slidesData.filter((item) => {
                return item.key && !item.key.includes('Editable');
            });
        case '7':
            return slidesData.filter((item) => {
                return item.key && !item.key.includes('Editable');
            });
        case '8':
        // Allow all
        // falls through
        default:
            return slidesData;
    }
};

export const filterUnitsData = (
    grade: string,
    unitsData: UnitDTO[],
    language?: string
): UnitDTO[] => {
    let allowedUnits = [];
    switch (grade) {
        case 'K':
        case '1':
            allowedUnits = ['1', '5', '6', '7', '8'];
            break;
        case '2':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7', '8'];
            break;
        case '3':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7'];
            break;
        case '4':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7'];
            break;
        case '5':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7'];
            break;
        case '6':
        case '6M':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7'];
            break;
        case '7':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7'];
            break;
        case '8':
            allowedUnits = ['1', '2', '3', '4', '5'];
            break;
        default:
            return unitsData;
    }

    let filteredUnitsData = unitsData.map((unit) => {
        if (!allowedUnits.includes(`${unit.unitNumber}`)) {
            unit.lessons = unit.lessons.map((lesson) => {
                if (!lesson.subitems) return lesson;

                lesson.subitems = lesson.subitems.filter((subitem) => {
                    return subitem.type && subitem.type !== 'Lesson Video';
                });

                return lesson;
            });
        }
        return unit;
    });

    //GK Spanish: filter out Strategy Videos and Lesson Videos
    if (grade === 'K' && language === 'ES') {
        filteredUnitsData = filteredUnitsData.map((unit) => {
            unit.lessons = unit.lessons.map((lesson) => {
                if (!lesson.subitems) return lesson;
                lesson.subitems = lesson.subitems.filter(
                    (subitem) =>
                        subitem.type !== 'Lesson Video'
                );
                return lesson;
            });
            return unit;
        });
    }

    //G3 and G6 Spanish: filter out Lesson Videos
    if ((grade === '3' || grade === '6') && language === 'ES') {
        filteredUnitsData = filteredUnitsData.map((unit) => {
            unit.lessons = unit.lessons.map((lesson) => {
                if (!lesson.subitems) return lesson;
                lesson.subitems = lesson.subitems.filter(
                    (subitem) => subitem.type !== 'Lesson Video'
                );
                return lesson;
            });
            return unit;
        });
    }

    return filteredUnitsData;
};

export const filterResourcesData = (
    assessmentsData: ResourceHeaderDTO[],
    grade: string,
    language?: string
): ResourceHeaderDTO[] => {
    let newResourcesData = [...assessmentsData];
    function removeActivityResources() {
        return newResourcesData.map((item: ResourceHeaderDTO) => {
            if (item.key === 'BTBFBox') {
                const fluencyWorksheets: ResourceItemDTO | undefined = item.items?.find(
                    (subitem: ResourceItemDTO) => subitem.text === 'Fluency Worksheets'
                );
                if (fluencyWorksheets) {
                    fluencyWorksheets.subitems = fluencyWorksheets.subitems?.filter(
                        (subitem: ResourceItemDTO) => !subitem.text.includes('Online Practice')
                    );
                }
            }
            return item;
        });
    }

    newResourcesData = removeActivityResources();

    function removeExemplarBox() {
        return newResourcesData.filter((item: ResourceHeaderDTO) => item.key !== 'exemplarsBox');
    }

    newResourcesData = removeExemplarBox();

    //Spanish Resources
    if (language === 'ES') {
        //GK: Filters out Calendar Routines
        if (grade === 'K') {
            function removeSpanishCalendarRoutines() {
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    item.items = item.items?.filter(
                        (subitem: ResourceItemDTO) => subitem.key !== 'CalendarRoutines'
                    );
                    return item;
                });
            }

            newResourcesData = removeSpanishCalendarRoutines();
        }

        //GK, G3, and G6: Filters out Family Letters
        if (grade === 'K' || grade === '3' || grade === '6') {
            function removeSpanishFamilyLetters() {
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    item.items = item.items?.filter(
                        (subitem: ResourceItemDTO) => subitem.key !== 'TeacherEditionFamilyLetters'
                    );
                    return item;
                });
            }

            newResourcesData = removeSpanishFamilyLetters();
        }

        //GK and G3: Filters out BTBF Lessons
        if (grade === 'K' || grade === '3') {
            function removeSpanishBTBFLessons() {
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'BTBFBox') {
                        item.items = item.items?.filter(
                            (subitem: ResourceItemDTO) => subitem.key !== 'BTBFLessons'
                        );
                    }
                    return item;
                });
            }

            newResourcesData = removeSpanishBTBFLessons();
        }

        //G3: Filters out BTBF Fluency Games
        if (grade === '3') {
            function removeG3SpanishBTBFFluencyGames() {
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'BTBFBox') {
                        item.items = item.items?.filter(
                            (subitem: ResourceItemDTO) => subitem.key !== 'BTBFFluencyGames'
                        );
                    }
                    return item;
                });
            }

            newResourcesData = removeG3SpanishBTBFFluencyGames();
        }

        //G3: Filters out specific BTBF Fluency Worksheet subitems (filtered by link)
        if (grade === '3') {
            function removeG3SpanishBTBFFluencySubitems() {
                const excludedLinks = ['Division_Facts_2_to_9', 'Multiplication_Facts_0_to_9'];
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'BTBFBox') {
                        const fluencyWorksheets = item.items?.find(
                            (subitem: ResourceItemDTO) => subitem.key === 'BTBFFluencyWorksheets'
                        );
                        if (fluencyWorksheets) {
                            fluencyWorksheets.subitems = fluencyWorksheets.subitems?.filter(
                                (subitem: ResourceItemDTO) =>
                                    !excludedLinks.some((link) => subitem.link?.includes(link))
                            );
                        }
                    }
                    return item;
                });
            }

            newResourcesData = removeG3SpanishBTBFFluencySubitems();
        }

        //GK: Filters out BTBF Fluency Worksheets, but keeps allowed ones
        if (grade === 'K') {
            function removeSpanishBTBFFluencyWorksheets() {
                const allowedSpanishFluencyItems = ['Suma Hasta 5', 'Resta Hasta 5'];
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'BTBFBox') {
                        const spanishFluencyWorksheets = item.items?.find(
                            (subitem: ResourceItemDTO) => subitem.key === 'BTBFFluencyWorksheets'
                        );
                        if (spanishFluencyWorksheets) {
                            spanishFluencyWorksheets.subitems =
                                spanishFluencyWorksheets.subitems?.filter(
                                    (subitem: ResourceItemDTO) =>
                                        allowedSpanishFluencyItems.includes(subitem.text)
                                );
                        }
                    }
                    return item;
                });
            }

            newResourcesData = removeSpanishBTBFFluencyWorksheets();
        }

        //G6: Filters out Activity Pages Keys
        if (grade === '6') {
            function removeG6ActivityPagesKeys() {
                return newResourcesData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'teacherEditionBox') {
                        item.items = item.items?.map((subitem: ResourceItemDTO) => {
                            if (subitem.key === 'TeacherEditionActivityResources') {
                                subitem.subitems = subitem.subitems?.filter(
                                    (s: ResourceItemDTO) => !s.link?.includes('Activity_Pages_Keys')
                                );
                            }
                            return subitem;
                        });
                    }
                    return item;
                });
            }

            newResourcesData = removeG6ActivityPagesKeys();
        }

        //G6: Filters out entire BTBF Box
        if (grade === '6') {
            function removeG6BTBFBox() {
                return newResourcesData.filter((item: ResourceHeaderDTO) => item.key !== 'BTBFBox');
            }

            newResourcesData = removeG6BTBFBox();
        }

        //GK, G3, and G6: Filters out Additional Resources Box
        if (grade === 'K' || grade === '3' || grade === '6') {
            function removeAdditionalResourcesBox() {
                return newResourcesData.filter(
                    (item: ResourceHeaderDTO) => item.key !== 'additionalResourcesBox'
                );
            }

            newResourcesData = removeAdditionalResourcesBox();
        }
    }

    return newResourcesData;
};

export const filterAssessmentsData = (
    assessmentsData: ResourceHeaderDTO[],
    grade: string,
    language?: string
): ResourceHeaderDTO[] => {
    let newAssessmentData = [...assessmentsData];
    function removeOnlineAssessments() {
        return newAssessmentData.filter(
            (item: ResourceHeaderDTO) => item.key !== 'onlineAssessmentsBox'
        );
    }
    function removeActivitiesByStandard() {
        return newAssessmentData.filter(
            (item: ResourceHeaderDTO) => item.key !== 'activitesByStandardBox'
        );
    }

    newAssessmentData = removeOnlineAssessments();
    newAssessmentData = removeActivitiesByStandard();

    //Spanish Assessments
    if (language === 'ES') {
        //GK: Filters out Open Ended Pretests (Preliminar X RA)
        if (grade === 'K') {
            function removeOpenEndedPretests() {
                return newAssessmentData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'PretestsBox') {
                        item.items = item.items?.map((pretest: ResourceItemDTO) => {
                            pretest.subitems = pretest.subitems?.filter(
                                (subitem: ResourceItemDTO) => !subitem.link?.includes('_OE')
                            );
                            return pretest;
                        });
                    }
                    return item;
                });
            }

            newAssessmentData = removeOpenEndedPretests();
        }

        //GK: Filters out Open Ended Unit Tests (Unidad X RA)
        if (grade === 'K') {
            function removeOpenEndedUnitTests() {
                return newAssessmentData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'unitTestsBox') {
                        item.items = item.items?.map((version: ResourceItemDTO) => {
                            version.subitems = version.subitems?.filter(
                                (subitem: ResourceItemDTO) => !subitem.link?.includes('_OE')
                            );
                            return version;
                        });
                    }
                    return item;
                });
            }

            newAssessmentData = removeOpenEndedUnitTests();
        }

        //GK: Filters out Open Ended Benchmarks (Evaluaciones de Referencia X RA)
        if (grade === 'K') {
            function removeOpenEndedBenchmarks() {
                return newAssessmentData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'BenchmarksBox') {
                        item.items = item.items?.map((version: ResourceItemDTO) => {
                            version.subitems = version.subitems?.filter(
                                (subitem: ResourceItemDTO) => !subitem.link?.includes('_OE')
                            );
                            return version;
                        });
                    }
                    return item;
                });
            }

            newAssessmentData = removeOpenEndedBenchmarks();
        }

        //GK: Filters out Open Ended Answer Keys (Claves de Respuestas RA)
        if (grade === 'K') {
            function removeOpenEndedAnswerKeys() {
                return newAssessmentData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'answerKeysBox') {
                        item.items = item.items?.filter(
                            (subitem: ResourceItemDTO) => !subitem.link?.includes('_OE')
                        );
                    }
                    return item;
                });
            }

            newAssessmentData = removeOpenEndedAnswerKeys();
        }

        //G6: Filters out Diagnostic Activities and Activity Sheets
        if (grade === '6') {
            function removeG6DiagnosticActivities() {
                return newAssessmentData.map((item: ResourceHeaderDTO) => {
                    if (item.key === 'diagnosticAssessmentsBox') {
                        item.items = item.items?.map((diagnostic: ResourceItemDTO) => {
                            diagnostic.subitems = diagnostic.subitems?.filter(
                                (subitem: ResourceItemDTO) =>
                                    !subitem.link?.includes('_Activities') &&
                                    !subitem.link?.includes('_Activity_Sheets')
                            );
                            return diagnostic;
                        });
                    }
                    return item;
                });
            }

            newAssessmentData = removeG6DiagnosticActivities();
        }
    }

    return newAssessmentData;
};

export const filterLanguageSetting = (grade: string, language: string): string => {
    if (['K', '3', '6'].includes(grade)) {
        return language;
    }
    return 'EN';
};
